import { motion, AnimatePresence } from 'framer-motion'
import { useState, useMemo } from 'react'
import { format, isWithinInterval, startOfMonth, endOfMonth, addMonths, subMonths, isAfter, startOfDay, endOfDay, isSameDay, isToday } from 'date-fns'
import { Calendar, Grid, List, Filter, Search, Download, Users, Star, ChevronLeft, ChevronRight, Infinity } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CalendarGrid } from './CalendarGrid'
import { CalendarWeek } from './CalendarWeek'
import { EventType } from './mockEventData'
import { cn } from '@/lib/utils'

interface CalendarViewProps {
  events: EventType[]
  onEventClick?: (event: EventType) => void
  className?: string
}

type CalendarViewMode = 'month' | 'week' | 'agenda'
type FilterType = 'all' | 'demo' | 'webinar' | 'workshop' | 'tour' | 'launch'

export function CalendarView({ events, onEventClick, className }: CalendarViewProps) {
  // Default to agenda view on mobile, month on desktop
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => {
    return typeof window !== 'undefined' && window.innerWidth < 1024 ? 'agenda' : 'month'
  })
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [showFilters, setShowFilters] = useState(false)
  // Default to showing all events on mobile for better experience
  const [showAllEvents, setShowAllEvents] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 1024
  })
  const [showTodayOnly, setShowTodayOnly] = useState(false)

  // Filter events
  const filteredEvents = useMemo(() => {
    let filtered = events

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(event => event.type === filterType)
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.presenter.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }, [events, filterType, searchTerm])

  // Get events for current month or all upcoming events (for agenda view)
  const agendaEvents = useMemo(() => {
    if (showTodayOnly) {
      // Show only today's events
      const dayStart = startOfDay(new Date())
      const dayEnd = endOfDay(new Date())
      return filteredEvents
        .filter(event => isWithinInterval(event.startTime, { start: dayStart, end: dayEnd }))
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    } else if (showAllEvents) {
      // Show all upcoming events
      const now = new Date()
      return filteredEvents
        .filter(event => isAfter(event.startTime, now) || isAfter(event.endTime, now))
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    } else {
      // Show only current month events
      const monthStart = startOfMonth(selectedDate)
      const monthEnd = endOfMonth(selectedDate)
      return filteredEvents
        .filter(event => isWithinInterval(event.startTime, { start: monthStart, end: monthEnd }))
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    }
  }, [filteredEvents, selectedDate, showAllEvents, showTodayOnly])

  const viewModes = [
    { id: 'month', label: 'Month', icon: Calendar, description: 'Full month grid view' },
    { id: 'week', label: 'Week', icon: Grid, description: 'Week timeline view' },
    { id: 'agenda', label: 'Agenda', icon: List, description: 'List of events' }
  ]

  const filterOptions = [
    { id: 'all', label: 'All Events', count: events.length },
    { id: 'demo', label: 'Demos', count: events.filter(e => e.type === 'demo').length },
    { id: 'webinar', label: 'Webinars', count: events.filter(e => e.type === 'webinar').length },
    { id: 'workshop', label: 'Workshops', count: events.filter(e => e.type === 'workshop').length },
    { id: 'tour', label: 'Tours', count: events.filter(e => e.type === 'tour').length },
    { id: 'launch', label: 'Launches', count: events.filter(e => e.type === 'launch').length }
  ]

  const getEventTypeColor = (type: EventType['type']) => {
    const colors = {
      demo: 'from-blue-500 to-cyan-500',
      webinar: 'from-purple-500 to-pink-500',
      workshop: 'from-orange-500 to-red-500',
      tour: 'from-green-500 to-teal-500',
      launch: 'from-yellow-400 to-orange-500'
    }
    return colors[type] || 'from-gray-500 to-gray-600'
  }

  return (
    <div className={cn("w-full space-y-6", className)}>
      {/* Calendar Controls */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        {/* Top Controls */}
        <div className="flex flex-col gap-4 mb-6">
          {/* View Mode Selector - Full width on mobile */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1 w-full sm:w-auto">
            {viewModes.map((mode) => (
              <motion.button
                key={mode.id}
                className={cn(
                  "flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors transition-shadow relative flex-1 sm:flex-none",
                  viewMode === mode.id
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                )}
                onClick={() => setViewMode(mode.id as CalendarViewMode)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <mode.icon className="w-4 h-4" />
                <span className="text-xs sm:text-sm">{mode.label}</span>

                {viewMode === mode.id && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg"
                    layoutId="activeCalendarView"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* Search and Filter - Responsive */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {/* Search - Full width on mobile */}
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              />
            </div>

            <div className="flex gap-3">
              {/* Filter Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "border-gray-200 dark:border-gray-600 flex-1 sm:flex-initial",
                  showFilters && "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600"
                )}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>

              {/* Export Button - Hidden on mobile */}
              <Button variant="outline" size="sm" className="border-gray-200 dark:border-gray-600 hidden sm:flex flex-1 sm:flex-initial">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Calendar Notice */}
        {(viewMode === 'month' || viewMode === 'week') && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="lg:hidden mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Better on Desktop
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Calendar grid is optimized for larger screens. Try <button onClick={() => setViewMode('agenda')} className="underline font-semibold">Agenda view</button> for better mobile experience.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-gray-200 dark:border-gray-700 pt-4"
            >
              <div className="flex flex-wrap gap-2">
                {filterOptions.map((option) => (
                  <motion.button
                    key={option.id}
                    className={cn(
                      "px-3 py-2 rounded-full text-sm font-medium transition-colors transition-shadow transition-border border",
                      filterType === option.id
                        ? "bg-blue-500 text-white border-blue-500 shadow-sm"
                        : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    )}
                    onClick={() => setFilterType(option.id as FilterType)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {option.label}
                    <span className="ml-2 opacity-75">({option.count})</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Calendar Content */}
      <AnimatePresence mode="wait">
        <div key={viewMode}>
          {viewMode === 'month' && (
            <CalendarGrid
              events={filteredEvents}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              onEventClick={onEventClick}
            />
          )}

          {viewMode === 'week' && (
            <CalendarWeek
              events={filteredEvents}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              onEventClick={onEventClick}
            />
          )}

          {viewMode === 'agenda' && (
            <div className="space-y-4">
              {/* Agenda Header */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {showTodayOnly 
                        ? 'Today\'s Events' 
                        : showAllEvents 
                          ? 'All Upcoming Events' 
                          : `${format(selectedDate, 'MMMM yyyy')} Agenda`
                      }
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {agendaEvents.length} events {showTodayOnly ? 'today' : showAllEvents ? 'upcoming' : 'scheduled'}
                    </p>
                  </div>

                  {/* Month Navigation & Show All Toggle */}
                  <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
                    {!showAllEvents && !showTodayOnly && (
                      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedDate(prev => subMonths(prev, 1))}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedDate(new Date())
                          }}
                          className="h-8 px-3 text-xs font-medium"
                        >
                          This Month
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedDate(prev => addMonths(prev, 1))}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    <Button
                      variant={showTodayOnly ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setShowTodayOnly(!showTodayOnly)
                        setShowAllEvents(false)
                        setSelectedDate(new Date())
                      }}
                      className={cn(
                        "flex-1 sm:flex-initial whitespace-nowrap",
                        showTodayOnly && "bg-green-600 hover:bg-green-700"
                      )}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {showTodayOnly ? 'Show More' : 'Today'}
                    </Button>

                    <Button
                      variant={showAllEvents ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (showAllEvents) {
                          // When switching from "Show All" to "Show Month", go to current month
                          setSelectedDate(new Date())
                        }
                        setShowTodayOnly(false)
                        setShowAllEvents(!showAllEvents)
                      }}
                      className={cn(
                        "flex-1 sm:flex-initial whitespace-nowrap",
                        showAllEvents && "bg-blue-600 hover:bg-blue-700"
                      )}
                    >
                      <Infinity className="w-4 h-4 mr-2" />
                      {showAllEvents ? 'Show Month' : 'Show All'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Events List */}
              {agendaEvents.length > 0 ? (
                <div className="space-y-3">
                  {agendaEvents.map((event, index) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => onEventClick?.(event)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Event Indicator */}
                        <div className="flex flex-col items-center">
                          <div className={cn(
                            "w-3 h-3 rounded-full bg-gradient-to-r",
                            getEventTypeColor(event.type)
                          )} />
                          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mt-2" />
                        </div>

                        {/* Event Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              event.type === 'demo' && "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                              event.type === 'webinar' && "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
                              event.type === 'workshop' && "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
                              event.type === 'tour' && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
                              event.type === 'launch' && "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300"
                            )}>
                              {event.type.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {format(event.startTime, 'EEE, MMM dd')}
                            </span>
                          </div>

                          <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {event.title}
                          </h4>

                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                            {event.description}
                          </p>

                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(event.startTime, 'HH:mm')} - {format(event.endTime, 'HH:mm')}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {event.currentAttendees}/{event.maxAttendees}
                            </div>
                            <div className="truncate">
                              {event.location}
                            </div>
                          </div>
                        </div>

                        {/* Event Status */}
                        <div className="text-right">
                          {event.isFeatured && (
                            <div className="inline-flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded-full text-xs font-medium mb-2">
                              <Star className="w-3 h-3" />
                              Featured
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {Math.round((event.currentAttendees / event.maxAttendees) * 100)}% Full
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No events found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
                    {searchTerm || filterType !== 'all'
                      ? "Try adjusting your search or filter criteria"
                      : showTodayOnly
                        ? "No events scheduled for today"
                        : showAllEvents
                          ? "No upcoming events available"
                          : `No events scheduled for ${format(selectedDate, 'MMMM yyyy')}`
                    }
                  </p>
                  {!showAllEvents && !showTodayOnly && !searchTerm && filterType === 'all' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAllEvents(true)}
                      className="mt-4"
                    >
                      <Infinity className="w-4 h-4 mr-2" />
                      View All Upcoming Events
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </AnimatePresence>
    </div>
  )
}